import React, { useMemo, useCallback } from 'react';
import { MoreVertical, GripVertical, Shield, Users } from 'lucide-react';
import { useDroppable } from '../KanbanDndContext';
import { formatDisplayDate } from '../../utils/dateUtils';
import { useAuth } from '../../contexts/AuthContext';
import { ProjectSidebarItemProps } from './types';

export const MemoIcon = React.memo(({ icon: Icon, size, className }: any) => (
  <Icon size={size} className={className} />
));

export const ProjectSidebarItem = React.memo(({ 
  project, 
  isActive, 
  isArchivedList, 
  isCollapsed, 
  isDraggingOver, 
  isOpen, 
  onSelect, 
  onPrefetch,
  onMenuToggle,
  onDragOver,
  onDragLeave,
  onDrop,
  onDuplicate,
  onRename,
  onArchiveToggle,
  onDelete,
  index,
  listLength,
  categories,
  onAssignCategory,
  isReadOnly = false
}: ProjectSidebarItemProps) => {
  const { user } = useAuth();
  const userId = user?.uid;

  const formattedDate = useMemo(() => 
    project.lastModified ? formatDisplayDate(new Date(project.lastModified)) : '---',
    [project.lastModified]
  );

  const prefetchTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = useCallback(() => {
    if (!onPrefetch) return;
    prefetchTimerRef.current = setTimeout(() => {
      onPrefetch(project.id);
    }, 100);
  }, [onPrefetch, project.id]);

  const handleMouseLeave = useCallback(() => {
    if (prefetchTimerRef.current) {
      clearTimeout(prefetchTimerRef.current);
    }
    onDragLeave?.();
  }, [onDragLeave]);

  const { setNodeRef, isOver } = useDroppable({
    id: `sidebar-project-${project.id}`,
    disabled: isArchivedList
  });

  return (
    <div 
      ref={setNodeRef}
      className={`group relative flex items-center gap-2 pl-3 pr-2.5 py-2.5 rounded-xl transition-all duration-200 ease-out cursor-pointer border hover:translate-x-[2px]
        ${isOpen ? 'z-[40]' : ''}
        ${isActive
          ? 'bg-indigo-50/70 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300 shadow-sm border-indigo-200/80 dark:border-indigo-850/50 shadow-indigo-500/5' 
          : (isOver 
              ? 'bg-indigo-100 dark:bg-indigo-900/60 border-indigo-400 dark:border-indigo-400 scale-[1.05] shadow-xl z-10 animate-pulse ring-4 ring-indigo-500/20' 
              : `${isArchivedList ? 'opacity-70 hover:opacity-100 italic' : ''} text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/40 hover:text-slate-700 dark:hover:text-slate-200 border-transparent hover:border-slate-200/25 dark:hover:border-slate-800/30`)}\
      `}
      style={{ willChange: 'transform, opacity', contain: 'layout' }}
      onClick={() => onSelect(project.id)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isActive && (
        <div className={`absolute left-0 top-2.5 bottom-2.5 w-[3px] rounded-r-full ${project.color || 'bg-slate-400'}`} />
      )}

      {isCollapsed ? (
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-xs shadow-sm shrink-0 ${project.color || 'bg-slate-400'}`}>
          {project.name.substring(0, 2).toUpperCase()}
        </div>
      ) : (
        <div className="flex items-center gap-1.5 shrink-0 pl-1">
          <div className={`w-2.5 h-2.5 rounded-full ${project.color || 'bg-slate-400'} transition-transform duration-200 group-hover:scale-110`} />
        </div>
      )}
      
      {!isCollapsed && (
        <div className="flex-1 min-w-0 flex items-center gap-1.5">
          <div className={`text-sm font-bold truncate tracking-tight transition-colors duration-200 group-hover:text-slate-900 dark:group-hover:text-slate-100 ${isActive ? 'text-indigo-900 dark:text-indigo-200' : ''} ${isArchivedList ? 'italic' : ''}`}>{project.name}</div>
          {((project.sharedWith && project.sharedWith.length > 0) || (project.userId && project.userId !== userId)) && (
            <div className="shrink-0 text-indigo-500/80 dark:text-indigo-405/80" title={project.userId !== userId ? "Geteiltes Projekt (Lese/Schreibzugriff)" : `Geteilt mit ${project.sharedWith?.join(', ')}`}>
              <Users size={12} className="stroke-[2.5px]" />
            </div>
          )}
        </div>
      )}

      {!isCollapsed && !isReadOnly && (
        <div className="flex items-center gap-0.5 shrink-0 relative">
          {!isArchivedList && (
            <div className="opacity-0 group-hover:opacity-40 transition-opacity cursor-grab active:cursor-grabbing p-1 text-slate-400 hover:text-slate-650 dark:hover:text-slate-200">
              <MemoIcon icon={GripVertical} size={14} />
            </div>
          )}

          <button 
            onClick={(e) => { 
              e.stopPropagation(); 
              onRename(project.id); 
            }}
            className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all hover:bg-white dark:hover:bg-slate-700 hover:shadow-sm opacity-0 group-hover:opacity-100"
            title="Projekt-Einstellungen öffnen"
          >
            <MemoIcon icon={MoreVertical} size={14} />
          </button>
        </div>
      )}

      {!isCollapsed && isReadOnly && (
        <div className="flex items-center gap-0.5 shrink-0 relative pr-2" title="Nur Lesezugriff">
          <MemoIcon icon={Shield} size={14} className="text-slate-400 dark:text-slate-500 opacity-60" />
        </div>
      )}
    </div>
  );
});
